import React from 'react';
import { ArrowRight, MapPin } from 'lucide-react';

const CITIES = [
  { name: 'New Orleans', slug: 'new-orleans' },
  { name: 'Metairie', slug: 'metairie' },
  { name: 'Kenner', slug: 'kenner' },
  { name: 'Slidell', slug: 'slidell' },
  { name: 'Covington', slug: 'covington' },
  { name: 'Mandeville', slug: 'mandeville' },
];

/** Internal links for AC problem pages → hub + estimate + city pages */
export const ProblemPageLinks = () => (
  <section className="py-12 bg-slate-50" data-testid="seo-internal-links">
    <div className="max-w-4xl mx-auto px-6">
      <h3 className="text-lg font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Explore More HVAC Resources
      </h3>
      <div className="grid sm:grid-cols-2 gap-3 mb-6">
        <a href="/common-ac-problems" className="flex items-center gap-2 p-3 bg-white rounded-lg border border-slate-200 hover:border-[#0EA5E9] transition-colors text-sm font-medium text-[#0F172A]">
          <ArrowRight className="w-4 h-4 text-[#0EA5E9]" /> All Common AC Problems
        </a>
        <a href="/instant-hvac-estimate-new-orleans" className="flex items-center gap-2 p-3 bg-white rounded-lg border border-slate-200 hover:border-[#F97316] transition-colors text-sm font-medium text-[#0F172A]">
          <ArrowRight className="w-4 h-4 text-[#F97316]" /> Get an Instant HVAC Estimate
        </a>
        <a href="/repair-vs-replace-ac" className="flex items-center gap-2 p-3 bg-white rounded-lg border border-slate-200 hover:border-[#0EA5E9] transition-colors text-sm font-medium text-[#0F172A]">
          <ArrowRight className="w-4 h-4 text-[#0EA5E9]" /> Repair vs. Replace Guide
        </a>
        <a href="/what-size-ac-unit-do-i-need" className="flex items-center gap-2 p-3 bg-white rounded-lg border border-slate-200 hover:border-[#0EA5E9] transition-colors text-sm font-medium text-[#0F172A]">
          <ArrowRight className="w-4 h-4 text-[#0EA5E9]" /> What Size AC Do I Need?
        </a>
      </div>
      <div className="flex flex-wrap gap-2">
        {CITIES.map(c => (
          <a key={c.slug} href={`/hvac/${c.slug}`} className="inline-flex items-center gap-1 px-3 py-1.5 bg-white rounded-full border border-slate-200 text-xs text-slate-600 hover:border-[#0EA5E9] hover:text-[#0EA5E9] transition-colors">
            <MapPin className="w-3 h-3" /> HVAC {c.name}
          </a>
        ))}
      </div>
    </div>
  </section>
);

/** Internal links for replacement/educational pages → pricing + estimate */
export const ReplacementPageLinks = () => (
  <section className="py-12 bg-slate-50" data-testid="seo-internal-links">
    <div className="max-w-4xl mx-auto px-6">
      <h3 className="text-lg font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Related HVAC Resources
      </h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <a href="/ac-replacement-cost-new-orleans" className="flex items-center gap-2 p-3 bg-white rounded-lg border border-slate-200 hover:border-[#0EA5E9] transition-colors text-sm font-medium text-[#0F172A]">
          <ArrowRight className="w-4 h-4 text-[#0EA5E9]" /> AC Replacement Costs
        </a>
        <a href="/hvac-pricing-new-orleans" className="flex items-center gap-2 p-3 bg-white rounded-lg border border-slate-200 hover:border-[#0EA5E9] transition-colors text-sm font-medium text-[#0F172A]">
          <ArrowRight className="w-4 h-4 text-[#0EA5E9]" /> HVAC Pricing Guide
        </a>
        <a href="/instant-hvac-estimate-new-orleans" className="flex items-center gap-2 p-3 bg-white rounded-lg border border-slate-200 hover:border-[#F97316] transition-colors text-sm font-medium text-[#0F172A]">
          <ArrowRight className="w-4 h-4 text-[#F97316]" /> Instant HVAC Estimate
        </a>
        <a href="/what-size-ac-unit-do-i-need" className="flex items-center gap-2 p-3 bg-white rounded-lg border border-slate-200 hover:border-[#0EA5E9] transition-colors text-sm font-medium text-[#0F172A]">
          <ArrowRight className="w-4 h-4 text-[#0EA5E9]" /> What Size AC Do I Need?
        </a>
      </div>
    </div>
  </section>
);

/** Internal links for city landing pages → estimate + cost + problems */
export const CityPageLinks = ({ cityName }) => (
  <section className="py-12 bg-white" data-testid="seo-city-links">
    <div className="max-w-4xl mx-auto px-6">
      <h3 className="text-lg font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
        HVAC Resources for {cityName} Homeowners
      </h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <a href="/instant-hvac-estimate-new-orleans" className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-[#F97316] transition-colors text-sm font-medium text-[#0F172A]">
          <ArrowRight className="w-4 h-4 text-[#F97316]" /> Instant HVAC Estimate
        </a>
        <a href="/ac-replacement-cost-new-orleans" className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-[#0EA5E9] transition-colors text-sm font-medium text-[#0F172A]">
          <ArrowRight className="w-4 h-4 text-[#0EA5E9]" /> AC Replacement Costs
        </a>
        <a href="/common-ac-problems" className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-[#0EA5E9] transition-colors text-sm font-medium text-[#0F172A]">
          <ArrowRight className="w-4 h-4 text-[#0EA5E9]" /> Common AC Problems
        </a>
        <a href="/compare-hvac-contractors" className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-[#0EA5E9] transition-colors text-sm font-medium text-[#0F172A]">
          <ArrowRight className="w-4 h-4 text-[#0EA5E9]" /> Compare HVAC Contractors
        </a>
      </div>
    </div>
  </section>
);
